import { Injectable, inject } from '@angular/core';
import { Observable, catchError, map, EMPTY } from 'rxjs';
import type { PostTxStatus } from '../models';
import { parsePostTxStatus } from '@shared/utils/post-tx-status.util';
import { TransactionService } from './transaction.service';

/**
 * TransactionStatusService
 *
 * Single source of truth for the post-transaction status lookup that
 * `SendStore` and `WithdrawalStore` previously duplicated verbatim: fetch the
 * transaction detail for a payment id, parse out the status, and swallow errors.
 *
 * Each dialog store keeps its own `txStatus` / `txDetailLoading` signals and
 * wires this into its own cancellable pipeline — only the fetch+parse is shared.
 *
 * Note on error semantics (preserved from the original stores): on failure the
 * stream completes empty, so the caller's `next` never fires. Callers that set a
 * loading flag before subscribing intentionally leave it set on error.
 */
@Injectable({ providedIn: 'root' })
export class TransactionStatusService {
  private readonly transactionService = inject(TransactionService);

  /**
   * Fetch and parse the post-transaction status for a payment.
   *
   * @param paymentId - The payment id returned by an init/transfer response
   * @returns Observable emitting the parsed {@link PostTxStatus} (or `null` when
   *          no status entry is present); emits nothing on error.
   */
  loadStatus(paymentId: string): Observable<PostTxStatus | null> {
    return this.transactionService.getTransactionDetail(paymentId).pipe(
      map((detail) => parsePostTxStatus(detail)),
      catchError(() => EMPTY),
    );
  }
}
